import type { ForwardedRef } from "react";

import type { InputProps } from "../props/input-props";
import type {
  ItemSelectorProps,
  ItemValueProps,
  MultiItemValueProps
} from "../props/item-props";

export const filterAlgorithms = {
  Includes: "includes",
  StartsWith: "startsWith"
} as const;

type FilterAlgorithm = (typeof filterAlgorithms)[keyof typeof filterAlgorithms];

export type AutocompleteProps<T> = BaseAutocompleteProps<T> & {
  /**
   * List of items the autocomplete can search for.
   *
   * If the items are objects, `getKey` and `getDisplayName` functions must be provided
   * to uniquely identify each item and to display them, respectively.
   */
  items: T[];
  /**
   * Choose between "includes" and "startsWith" for the filtering algorithm.
   * @default "includes"
   * */
  filterAlgorithm?: FilterAlgorithm;
};

export type AsyncAutocompleteProps<T> = BaseAutocompleteProps<T> & {
  /**
   * Function to fetch items based on the query string. It is the responsibility of the application to filter the items based on the query and only return the relevant items.
   * The application should also set a max item limit to avoid returning too many items, this can be done in componation with the minQueryLength prop.
   * @param query - The search query string.
   * @returns A promise that resolves to an array of items.
   */
  onQuery: (query: string) => Promise<T[]>;
  /**
   * Minimum number of characters required in the query string before triggering the search.
   * @default 3
   */
  minQueryLength?: number;
  /**
   * Time in milliseconds to debounce the search query.
   * This helps to limit the number of search requests made while typing.
   * @default 500
   */
  debounceTime?: number;
};

type BaseAutocompleteProps<T> = ItemValueProps<T> &
  ItemSelectorProps<T> &
  Pick<MultiItemValueProps<T>, "isRemovable"> &
  Pick<ItemValueProps<T>, "multiselect"> &
  Omit<
    InputProps,
    "defaultValue" | "value" | "onChange" | "htmlFor" | "errorId" | "multiple"
  > & {
    /**
     * Message displayed when the search query does not return any items.
     *
     * This is useful for providing feedback to the user when no matches are found.
     * @default "Ingen resultater funnet"
     */
    noResultsMessage?: string;
    /**
     * Function that is called with the item(s) that were removed. Applicable in multiselect mode.
     *
     * Removal can occur through interaction, such as clicking a "chip" representing an item, or by re-selecting an already selected item.
     */
    onRemoved?: (items: T[]) => unknown;
  };

export type ExtendedBaseAutocompleteProps<T> = BaseAutocompleteProps<T> & {
  onQuery: (query: string) => Promise<T[]>;
  minQueryLength?: number;
  debounceTime?: number;
  items?: T[];
  ref: ForwardedRef<HTMLInputElement>;
};
